import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  ChartSettingsTemplate, ChartSettingsPayload,
  chartSettingsService, DEFAULT_CHART_SETTINGS, resolvePayload, applySettingsOverride
} from '../services/chartSettings.service';

interface Ctx {
  templates: ChartSettingsTemplate[];
  active: ChartSettingsTemplate | null;
  /** Resolved, fully-populated settings (defaults merged). Always non-null. */
  settings: ChartSettingsPayload;
  loading: boolean;
  refresh: () => Promise<void>;
  createTemplate: (name: string, payload?: Partial<ChartSettingsPayload>, activate?: boolean) => Promise<ChartSettingsTemplate>;
  updateTemplate: (id: number, patch: { name?: string; payload?: Partial<ChartSettingsPayload> }) => Promise<ChartSettingsTemplate>;
  deleteTemplate: (id: number) => Promise<void>;
  activateTemplate: (id: number) => Promise<ChartSettingsTemplate>;
}

const ChartSettingsContext = createContext<Ctx | null>(null);

export const ChartSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [templates, setTemplates] = useState<ChartSettingsTemplate[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await chartSettingsService.list();
      setTemplates(rows);
    } catch {
      // Keep using defaults on fetch failure; no toast — the chart keeps working
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const active = useMemo(() => templates.find(t => t.isActive) || null, [templates]);
  const settings = useMemo(
    // applySettingsOverride is the read-only probe seam (see chartSettings.service):
    // it is a no-op unless `chart_settings_override_v1` is present in localStorage,
    // and application code never writes that key.
    () => applySettingsOverride(active ? resolvePayload(active.payload) : DEFAULT_CHART_SETTINGS),
    [active]
  );

  const createTemplate = useCallback(async (name: string, payload?: Partial<ChartSettingsPayload>, activate = false) => {
    const t = await chartSettingsService.create(name, payload || {}, activate);
    await refresh();
    return t;
  }, [refresh]);

  const updateTemplate = useCallback(async (id: number, patch: { name?: string; payload?: Partial<ChartSettingsPayload> }) => {
    const t = await chartSettingsService.update(id, patch);
    await refresh();
    return t;
  }, [refresh]);

  const deleteTemplate = useCallback(async (id: number) => {
    await chartSettingsService.remove(id);
    await refresh();
  }, [refresh]);

  const activateTemplate = useCallback(async (id: number) => {
    const t = await chartSettingsService.activate(id);
    await refresh();
    return t;
  }, [refresh]);

  const value: Ctx = {
    templates, active, settings, loading,
    refresh, createTemplate, updateTemplate, deleteTemplate, activateTemplate
  };
  return <ChartSettingsContext.Provider value={value}>{children}</ChartSettingsContext.Provider>;
};

export function useChartSettings(): Ctx {
  const ctx = useContext(ChartSettingsContext);
  if (!ctx) {
    // Not yet mounted inside provider — return a no-op fallback so charts render
    // with defaults (important for tests and any standalone usage).
    return {
      templates: [], active: null, settings: DEFAULT_CHART_SETTINGS, loading: false,
      refresh: async () => {},
      createTemplate: async () => { throw new Error('ChartSettingsProvider not mounted'); },
      updateTemplate: async () => { throw new Error('ChartSettingsProvider not mounted'); },
      deleteTemplate: async () => { throw new Error('ChartSettingsProvider not mounted'); },
      activateTemplate: async () => { throw new Error('ChartSettingsProvider not mounted'); },
    };
  }
  return ctx;
}
